import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  ArrowLeft, 
  CalendarIcon,
  Plus,
  Trash2,
  AlertCircle,
  CheckCircle2
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { HelpPanel } from '../components/ui/help-panel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEBIT_CREDIT_ROWS = [
  { type: 'Assets', example: 'Cash, Equipment, A/R', debit: '+ Increase', credit: '- Decrease', dColor: 'text-emerald-600 dark:text-emerald-400', cColor: 'text-red-500 dark:text-red-400' },
  { type: 'Liabilities', example: 'A/P, Loans, Credit Cards', debit: '- Decrease', credit: '+ Increase', dColor: 'text-red-500 dark:text-red-400', cColor: 'text-emerald-600 dark:text-emerald-400' },
  { type: 'Equity', example: "Owner's Equity, Retained Earnings", debit: '- Decrease', credit: '+ Increase', dColor: 'text-red-500 dark:text-red-400', cColor: 'text-emerald-600 dark:text-emerald-400' },
  { type: 'Revenue', example: 'Sales, Service Income', debit: '- Decrease', credit: '+ Increase', dColor: 'text-red-500 dark:text-red-400', cColor: 'text-emerald-600 dark:text-emerald-400' },
  { type: 'Expenses', example: 'Rent, Utilities, Payroll', debit: '+ Increase', credit: '- Decrease', dColor: 'text-emerald-600 dark:text-emerald-400', cColor: 'text-red-500 dark:text-red-400' },
];

const debitCreditHelp = {
  title: "Debit & Credit Guide",
  description: "Quick reference for how debits and credits affect each account type",
  sections: [
    {
      heading: "The Golden Rule",
      content: (
        <div className="space-y-2">
          <p>Every journal entry must have <strong>equal debits and credits</strong>. This is the foundation of double-entry accounting.</p>
          <div className="rounded-lg border overflow-hidden mt-3">
            <table className="w-full text-xs">
              <thead><tr className="bg-muted/50"><th className="text-left p-2 font-semibold">Account Type</th><th className="text-center p-2 font-semibold">Debit</th><th className="text-center p-2 font-semibold">Credit</th></tr></thead>
              <tbody>
                {DEBIT_CREDIT_ROWS.map(r => (
                  <tr key={r.type} className="border-t"><td className="p-2"><div className="font-medium">{r.type}</div><div className="text-muted-foreground text-[10px]">{r.example}</div></td><td className={`p-2 text-center font-semibold ${r.dColor}`}>{r.debit}</td><td className={`p-2 text-center font-semibold ${r.cColor}`}>{r.credit}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ),
    },
    {
      heading: "Common Examples",
      type: "info",
      content: (
        <div className="space-y-3 text-xs">
          <div className="p-2 rounded border bg-muted/30">
            <p className="font-medium mb-1">Paying rent ($1,000):</p>
            <p><span className="text-emerald-600 dark:text-emerald-400">Debit: Rent Expense +$1,000</span> / <span className="text-red-500 dark:text-red-400">Credit: Cash -$1,000</span></p>
          </div>
          <div className="p-2 rounded border bg-muted/30">
            <p className="font-medium mb-1">Customer pays invoice ($500):</p>
            <p><span className="text-emerald-600 dark:text-emerald-400">Debit: Cash +$500</span> / <span className="text-red-500 dark:text-red-400">Credit: A/R -$500</span></p>
          </div>
        </div>
      ),
    },
    {
      heading: "Memory Trick",
      content: (
        <div className="space-y-2">
          <div className="p-3 rounded-lg bg-primary/5 border border-primary/20 text-center font-mono text-sm font-semibold">Assets = Liabilities + Equity</div>
          <p className="text-xs"><strong>Left side</strong> (Assets): Debits increase. <strong>Right side</strong> (Liabilities, Equity, Revenue): Credits increase. <strong>Expenses</strong> are opposite of Revenue: Debits increase.</p>
        </div>
      ),
    },
  ],
  tips: [
    "Debits go on the left, Credits go on the right",
    "Total debits MUST equal total credits",
    "When in doubt, think about what's happening to Cash",
  ],
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(amount || 0);
};

export default function CreateJournalEntry() {
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [txnDate, setTxnDate] = useState(new Date());
  const [memo, setMemo] = useState('');
  const [lines, setLines] = useState([
    { account_id: '', description: '', debit: '', credit: '' },
    { account_id: '', description: '', debit: '', credit: '' },
  ]);

  useEffect(() => {
    const fetchAccounts = async () => {
      if (!currentWorkspace) return;
      
      try {
        const response = await fetch(
          `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/accounts?active_only=true`
        );
        
        if (response.ok) {
          const data = await response.json();
          setAccounts(data);
        }
      } catch (error) {
        console.error('Failed to fetch accounts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAccounts();
  }, [currentWorkspace]);

  const addLine = () => {
    setLines([...lines, { account_id: '', description: '', debit: '', credit: '' }]);
  };

  const removeLine = (index) => {
    if (lines.length <= 2) {
      toast.error('Journal entry must have at least 2 lines');
      return;
    }
    setLines(lines.filter((_, i) => i !== index));
  };

  const updateLine = (index, field, value) => {
    const newLines = [...lines];
    
    // If entering debit, clear credit and vice versa
    if (field === 'debit' && value) {
      newLines[index] = { ...newLines[index], debit: value, credit: '' };
    } else if (field === 'credit' && value) {
      newLines[index] = { ...newLines[index], credit: value, debit: '' };
    } else {
      newLines[index] = { ...newLines[index], [field]: value };
    }
    
    setLines(newLines);
  };

  const totalDebit = lines.reduce((sum, line) => sum + (parseFloat(line.debit) || 0), 0);
  const totalCredit = lines.reduce((sum, line) => sum + (parseFloat(line.credit) || 0), 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;
  const difference = Math.abs(totalDebit - totalCredit);

  const handleSave = async (andPost = false) => {
    // Validate
    const validLines = lines.filter(l => l.account_id && (l.debit || l.credit));
    
    if (validLines.length < 2) {
      toast.error('Entry must have at least 2 valid lines');
      return;
    }

    if (andPost && !isBalanced) {
      toast.error('Entry must be balanced to post');
      return;
    }

    setSaving(true);
    try {
      const entryData = {
        txn_date: format(txnDate, 'yyyy-MM-dd'),
        memo: memo || null,
        lines: validLines.map(line => ({
          account_id: line.account_id,
          description: line.description || null,
          debit: parseFloat(line.debit) || 0,
          credit: parseFloat(line.credit) || 0,
        })),
      };

      const response = await fetch(
        `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/journal-entries`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(entryData),
        }
      );

      if (response.ok) {
        const entry = await response.json();
        
        if (andPost) {
          // Post the entry
          const postResponse = await fetch(
            `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/journal-entries/${entry.entry_id}/post`,
            {
              method: 'POST',
              }
          );

          if (postResponse.ok) {
            toast.success('Entry created and posted');
          } else {
            toast.success('Entry created but failed to post');
          }
        } else {
          toast.success('Entry saved as draft');
        }
        
        navigate(`/journal-entries/${entry.entry_id}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create entry');
      }
    } catch (error) {
      console.error('Failed to create entry:', error);
      toast.error('Failed to create entry');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="create-journal-entry">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/journal-entries">
              <ArrowLeft className="w-5 h-5" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
              New Journal Entry
            </h1>
            <p className="text-muted-foreground">
              Create a new journal entry with balanced debits and credits
            </p>
          </div>
        </div>
        <HelpPanel {...debitCreditHelp} />
      </div>

      {/* Entry Header */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>Transaction Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                    data-testid="txn-date-picker"
                  >
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {format(txnDate, 'PPP')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={txnDate}
                    onSelect={(date) => date && setTxnDate(date)}
                  />
                </PopoverContent>
              </Popover>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="memo">Memo (Optional)</Label>
              <Textarea
                id="memo"
                placeholder="Description of this journal entry..."
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                rows={2}
                data-testid="memo-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lines */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Journal Lines</CardTitle>
          <Button variant="outline" size="sm" onClick={addLine} data-testid="add-line-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Line
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Column Headers */}
            <div className="hidden md:grid md:grid-cols-12 gap-4 text-sm font-medium text-muted-foreground px-2">
              <div className="col-span-4">Account</div>
              <div className="col-span-3">Description</div>
              <div className="col-span-2 text-right">Debit</div>
              <div className="col-span-2 text-right">Credit</div>
              <div className="col-span-1"></div>
            </div>

            {/* Lines */}
            {lines.map((line, index) => (
              <div 
                key={index} 
                className="grid grid-cols-1 md:grid-cols-12 gap-4 p-4 rounded-lg bg-muted/30 border border-border/50"
                data-testid={`line-${index}`}
              >
                <div className="md:col-span-4">
                  <Label className="md:hidden text-xs mb-1">Account</Label>
                  <Select
                    value={line.account_id}
                    onValueChange={(v) => updateLine(index, 'account_id', v)}
                  >
                    <SelectTrigger data-testid={`account-select-${index}`}>
                      <SelectValue placeholder="Select account" />
                    </SelectTrigger>
                    <SelectContent>
                      {accounts.map((account) => (
                        <SelectItem key={account.account_id} value={account.account_id}>
                          <span className="font-mono">{account.code}</span> - {account.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="md:col-span-3">
                  <Label className="md:hidden text-xs mb-1">Description</Label>
                  <Input
                    placeholder="Line description"
                    value={line.description}
                    onChange={(e) => updateLine(index, 'description', e.target.value)}
                    data-testid={`description-${index}`}
                  />
                </div>
                
                <div className="md:col-span-2">
                  <Label className="md:hidden text-xs mb-1">Debit</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={line.debit}
                    onChange={(e) => updateLine(index, 'debit', e.target.value)}
                    className={cn(
                      "text-right font-mono",
                      line.debit && "text-emerald-600 dark:text-emerald-400"
                    )}
                    data-testid={`debit-${index}`}
                  />
                </div>
                
                <div className="md:col-span-2">
                  <Label className="md:hidden text-xs mb-1">Credit</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={line.credit}
                    onChange={(e) => updateLine(index, 'credit', e.target.value)}
                    className={cn(
                      "text-right font-mono",
                      line.credit && "text-rose-600 dark:text-rose-400"
                    )}
                    data-testid={`credit-${index}`}
                  />
                </div>
                
                <div className="md:col-span-1 flex items-center justify-end">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeLine(index)}
                    disabled={lines.length <= 2}
                    data-testid={`remove-line-${index}`}
                  >
                    <Trash2 className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </div>
              </div>
            ))}

            {/* Totals */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 pt-4 border-t">
              <div className="md:col-span-7 flex items-center">
                {isBalanced ? (
                  <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="font-medium">Entry is balanced</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-destructive">
                    <AlertCircle className="w-5 h-5" />
                    <span className="font-medium">
                      Out of balance by {formatCurrency(difference)}
                    </span>
                  </div>
                )}
              </div>
              <div className="md:col-span-2 text-right">
                <p className="text-xs text-muted-foreground">Total Debit</p>
                <p className="font-mono font-semibold text-lg debit-amount">
                  {formatCurrency(totalDebit)}
                </p>
              </div>
              <div className="md:col-span-2 text-right">
                <p className="text-xs text-muted-foreground">Total Credit</p>
                <p className="font-mono font-semibold text-lg credit-amount">
                  {formatCurrency(totalCredit)}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-4 justify-end">
        <Button
          variant="outline"
          onClick={() => navigate('/journal-entries')}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button
          variant="secondary"
          onClick={() => handleSave(false)}
          disabled={saving}
          data-testid="save-draft-btn"
        >
          {saving ? 'Saving...' : 'Save as Draft'}
        </Button>
        <Button
          onClick={() => handleSave(true)}
          disabled={saving || !isBalanced}
          data-testid="save-post-btn"
        >
          {saving ? 'Saving...' : 'Save & Post'}
        </Button>
      </div>
    </div>
  );
}
